import axios from "axios";
import { sanitizeGuidelineHtml } from "./guideline-markdown";
import type {
  AggregatedIcdCode,
  AhaGuideline,
  AhaQaPair,
  ClinicalCriteriaFileContent,
  ClinicalCriteriaFileMeta,
  CmIndexTermNode,
  CmSearchHit,
  CmTableTermNode,
  CmTabularNoteHit,
  CmTabularNotes,
  CmTabularResult,
  CmTabularSearchHit,
  CodeSearchResult,
  EncoderCatalog,
  EncoderCatalogTab,
  EncoderCodeType,
  EncoderFhirPrefill,
  EncoderIcdValidationResult,
  EncoderLabeledOption,
  EncoderScratchCode,
  EncoderRouteId,
  EncoderTabId,
  IcdGuideline,
  MsDrgSex,
  PcsDefinition,
  CptEditsResult,
} from "../types/encoder";
import { RouteConstants } from "../constants/route-constant";

export const DEFAULT_ENCODER_TABS: EncoderCatalogTab[] = [
  { id: "code-search", label: "Code Search" },
  { id: "coding-guide", label: "Coding Guide" },
  { id: "clinical-criteria", label: "Clinical Criterias" },
  { id: "cm-explorer", label: "ICD-10-CM Builder" },
  { id: "pcs-explorer", label: "ICD-10-PCS Builder" },
  { id: "editor", label: "Code Editor" },
  { id: "ms-drg-grouper", label: "MS-DRG Grouper" },
  { id: "mcd-coverage", label: "MCD Coverage" },
];

export const uniqueSortedGuidelineIds = (
  ids: Array<number | string>
): number[] => {
  const seen: Record<number, true> = {};
  const unique: number[] = [];
  for (let i = 0; i < ids.length; i += 1) {
    const raw = ids[i];
    const id =
      typeof raw === "number" ? raw : Number.parseInt(String(raw), 10);
    if (!Number.isFinite(id) || id <= 0 || seen[id]) continue;
    seen[id] = true;
    unique.push(id);
  }
  unique.sort((a, b) => a - b);
  return unique;
};

export const GUIDELINE_BY_IDS_CHUNK_SIZE = 100;
export const DEFAULT_CODE_YEARS = [2026, 2025, 2024, 2023, 2022];
export const DEFAULT_CODE_TYPES: EncoderCodeType[] = [
  "All",
  "Dx",
  "PCS",
  "CPT",
  "HCPCS",
];

export const ENCODER_DX_TYPES = ["Dx", "SDX", "PDX", "ADX"];

export const isEncoderDxType = (type: string): boolean =>
  ENCODER_DX_TYPES.indexOf(type) >= 0;

export const isEncoderIcdResultType = (type: string): boolean =>
  isEncoderDxType(type) || type === "PCS";

export const isEncoderCptResultType = (type: string): boolean =>
  type === "CPT" || type === "HCPCS";

export const isEncoderCptClaimType = (type: string): boolean =>
  isEncoderCptResultType(type) || /^E[\s/-]?M$/i.test(type);

export const encoderDxTypeLabel = (type: string): string =>
  isEncoderDxType(type) ? "Dx" : type;

export const normalizeEncoderCode = (code: string): string =>
  code.replace(/\./g, "").replace(/\s+/g, "").toUpperCase();

/** Keep the scratchpad/search form. PSI accepts B160; encoder v1 examples also use B16.0. */
export const uniqueEncoderInstructionCodes = (codes: string[]): string[] => {
  const out: string[] = [];
  const seen: Record<string, boolean> = {};
  for (let i = 0; i < codes.length; i += 1) {
    const raw = (codes[i] || "").trim();
    if (!raw) continue;
    const key = normalizeEncoderCode(raw);
    if (!key || seen[key]) continue;
    seen[key] = true;
    out.push(raw);
  }
  return out;
};

export const DEFAULT_LABEL_COLORS = {
  MCC: { background: "#F3AC3F", color: "#000000" },
  HCC: { background: "#ff672e", color: "#ffffff" },
  CC: { background: "#f3d540", color: "#000000" },
  PDX: { background: "#1d4ed8", color: "#ffffff" },
  HAC: { background: "#ef4444", color: "#ffffff" },
  MC: { background: "#f97316", color: "#ffffff" },
  Rx: { background: "#93c5fd", color: "#000000" },
  SDOH: { background: "#3b82f6", color: "#ffffff" },
  ELIX: { background: "#166534", color: "#ffffff" },
  "IP Only": { background: "#f97316", color: "#ffffff" },
} as const;

export const LABEL_COLOR_CLASSES = {
  MCC: "bg-[#F3AC3F] text-black",
  HCC: "bg-[#ff672e] text-white",
  CC: "bg-[#f3d540] text-black",
  PDX: "bg-[#1d4ed8] text-white",
  HAC: "bg-red-500 text-white",
  MC: "bg-orange-500 text-white",
  Rx: "bg-blue-300 text-black",
  SDOH: "bg-blue-500 text-white",
  ELIX: "bg-green-800 text-white",
  "IP Only": "bg-orange-500 text-white",
} as const;

export const DEFAULT_POA_OPTIONS: EncoderLabeledOption[] = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
  { value: "U", label: "Unknown" },
  { value: "W", label: "Clinically Undetermined" },
  { value: "E", label: "Exempt" },
];

export const DEFAULT_SEX_OPTIONS: EncoderLabeledOption[] = [
  { value: "FEMALE", label: "Female" },
  { value: "MALE", label: "Male" },
  { value: "UNKNOWN", label: "Unknown" },
];

export const DEFAULT_DISCHARGE_DISPOSITIONS: EncoderLabeledOption[] = [
  { value: "HOME_SELFCARE_ROUTINE", code: "1", label: "Home/self-care, routine" },
  { value: "SHORT_TERM_HOSPITAL", code: "2", label: "Short-term hospital" },
  { value: "SNF", code: "3", label: "Skilled nursing facility" },
  { value: "CUST_SUPP_CARE", code: "4", label: "Custodial/supportive care" },
  { value: "CANC_CHILD_HOSP", code: "5", label: "Cancer/children hospital" },
  { value: "HOME_HEALTH_SERVICE", code: "6", label: "Home health service" },
  {
    value: "LEFT_AGAINST_MEDICAL_ADVICE",
    code: "7",
    label: "Left against medical advice",
  },
  { value: "DIED", code: "20", label: "Died" },
  { value: "COURT_LAW_ENFRC", code: "21", label: "Court/law enforcement" },
  { value: "STILL_A_PATIENT", code: "30", label: "Still a patient" },
  { value: "HOSPICE_HOME", code: "50", label: "Hospice, home" },
];

export const HAC_USAGE_MEANINGS: Record<string, string> = {
  HAC_01: "Foreign object retained after surgery.",
  HAC_02: "Air embolism.",
  HAC_03: "Blood incompatibility.",
  HAC_04: "Stage III and IV pressure ulcers.",
  HAC_05: "Falls and trauma.",
  HAC_06: "Catheter-associated urinary tract infection.",
  HAC_07: "Vascular catheter-associated infection.",
  HAC_08: "Surgical-site infection following certain CABG procedures.",
  HAC_09: "Manifestations of poor glycemic control.",
  HAC_10: "DVT/PE following certain knee or hip replacement procedures.",
  HAC_11: "Surgical-site infection following bariatric surgery.",
  HAC_12:
    "Surgical-site infection following certain spine, shoulder, or elbow procedures.",
  HAC_13:
    "Surgical-site infection following cardiac implantable-device procedures.",
  HAC_14:
    "Iatrogenic pneumothorax following certain venous catheterization procedures.",
};

export const AHA_QUARTER_CHIPS = [
  { id: "Q1", label: "Q1", value: "First Quarter" },
  { id: "Q2", label: "Q2", value: "Second Quarter" },
  { id: "Q3", label: "Q3", value: "Third Quarter" },
  { id: "Q4", label: "Q4", value: "Fourth Quarter" },
] as const;

export const mapAhaQuarterToDb = (quarter: string): string => {
  const chip = AHA_QUARTER_CHIPS.find((item) => item.id === quarter);
  return chip?.value ?? quarter;
};

export const formatEncoderProfileDate = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return `${match[2]}-${match[3]}-${match[1]}`;
};

export const formatIcdGuidelineHeader = (result: IcdGuideline): string => {
  const parts: string[] = [];
  const chapter = sanitizeGuidelineText(result.chapter);
  const subChapter = sanitizeGuidelineText(result.subChapter);
  const topic = sanitizeGuidelineText(result.topic);
  const isAppendix = chapter.toLowerCase().trim().startsWith("appendix");

  if (chapter) parts.push(chapter);
  if (subChapter) parts.push(subChapter);
  if (topic) parts.push(isAppendix ? `(${topic})` : topic);

  return parts.join(isAppendix ? ". " : ", ") || "Guideline";
};

export const DEFAULT_ENCODER_CATALOG: EncoderCatalog = {
  success: true,
  title: "Bulwark Encoder",
  description:
    "Code Editor Find ICD-10, CPT codes, and perform validation checks with AI ",
  tabs: DEFAULT_ENCODER_TABS,
  codeYears: DEFAULT_CODE_YEARS,
  codeTypes: DEFAULT_CODE_TYPES,
  labelColors: { ...DEFAULT_LABEL_COLORS },
  poaOptions: DEFAULT_POA_OPTIONS,
  sexOptions: DEFAULT_SEX_OPTIONS,
  dischargeDispositions: DEFAULT_DISCHARGE_DISPOSITIONS,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const cn = (
  ...classes: Array<string | false | null | undefined>
): string => classes.filter(Boolean).join(" ");

/** Copy plain text even when the Clipboard API is blocked (SMART iframe). */
export const copyPlainText = async (text: string): Promise<boolean> => {
  const value = text.trim();
  if (!value) return false;
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Permissions-Policy in an embedded EHR frame blocks clipboard.writeText.
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

const encoderBodyKeys = [
  "results",
  "trees",
  "years",
  "tabs",
  "tree",
  "result",
  "module",
  "description",
  "definition",
];

const nestedEncoderKeys = [
  "details",
  "grouperDetails",
  "indices",
  "triggered_measures",
  "suppressed_measures",
  "rejected_measures",
  "admissionDiagnosis",
  "diagnoses",
  "procedures",
  "versionUsed",
  "editType",
  "editCounts",
];

const hasEncoderBody = (value: Record<string, unknown>): boolean =>
  encoderBodyKeys.some((key) => value[key] != null);

export const unwrapEncoderPayload = (
  payload: unknown
): Record<string, unknown> | null => {
  if (!isRecord(payload)) return null;
  const nested = isRecord(payload.data) ? payload.data : null;
  if (
    nested &&
    !hasEncoderBody(payload) &&
    (hasEncoderBody(nested) || "success" in nested)
  ) {
    return unwrapEncoderPayload(nested);
  }
  if (!nested) return payload;
  const merged: Record<string, unknown> = { ...payload };
  for (let i = 0; i < nestedEncoderKeys.length; i += 1) {
    const key = nestedEncoderKeys[i];
    if (merged[key] == null && nested[key] != null) {
      merged[key] = nested[key];
    }
  }
  return merged;
};

const listFrom = (
  data: Record<string, unknown> | null,
  key: string
): unknown[] => {
  if (!data) return [];
  const value = data[key];
  if (Array.isArray(value)) return value;
  if (isRecord(data.data) && Array.isArray(data.data[key])) {
    return data.data[key] as unknown[];
  }
  if (
    (key === "results" || key === "trees" || key === "years") &&
    Array.isArray(data.data)
  ) {
    return data.data;
  }
  return [];
};

export const encoderErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (axios.isAxiosError(error)) {
    const data = unwrapEncoderPayload(error.response?.data);
    const nested = data && typeof data.data === "object" && data.data
      ? (data.data as Record<string, unknown>)
      : null;
    const candidates = [
      data?.error,
      nested?.error,
      data?.detail,
      nested?.detail,
      data?.message,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const value = candidates[i];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    const status = error.response?.status;
    if (status === 502) {
      return "MS-DRG grouping service returned 502. The hop could not complete grouping.";
    }
    if (typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const optionalString = (...values: unknown[]): string | null => {
  for (let i = 0; i < values.length; i += 1) {
    const text = asString(values[i]).trim();
    if (text) return text;
  }
  return null;
};

const listEncoderResults = (payload: unknown): unknown[] => {
  const data = unwrapEncoderPayload(payload);
  if (!data) return [];
  if (Array.isArray(data.results)) return data.results;
  if (isRecord(data.data) && Array.isArray(data.data.results)) {
    return data.data.results;
  }
  return [];
};

const asNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asStringArray = (value: unknown, depth = 0): string[] => {
  if (depth > 4 || value == null) return [];
  if (typeof value === "string") {
    const text = value.trim();
    return text ? [text] : [];
  }
  if (Array.isArray(value)) {
    const out: string[] = [];
    for (let i = 0; i < value.length; i += 1) {
      const nested = asStringArray(value[i], depth + 1);
      for (let j = 0; j < nested.length; j += 1) out.push(nested[j]);
    }
    return out;
  }
  if (isRecord(value)) {
    const direct = asString(
      value.text || value.note || value.description || value.value
    ).trim();
    if (direct) return [direct];
    if (value.notes != null) return asStringArray(value.notes, depth + 1);
    if (value.items != null) return asStringArray(value.items, depth + 1);
    if (value.values != null) return asStringArray(value.values, depth + 1);
    if (value.list != null) return asStringArray(value.list, depth + 1);
  }
  return [];
};

export const extractPcsCodeDescription = (payload: unknown): string | null => {
  const data = unwrapEncoderPayload(payload);
  if (!data) return null;
  const nested = isRecord(data.data) ? data.data : null;
  return (
    optionalString(data.description) ||
    (nested ? optionalString(nested.description) : null)
  );
};

export const extractPcsDefinition = (
  payload: unknown
): PcsDefinition | null => {
  const data = unwrapEncoderPayload(payload);
  if (!data) return null;
  const nested = isRecord(data.data) ? data.data : null;
  const raw = isRecord(data.definition)
    ? data.definition
    : nested && isRecord(nested.definition)
      ? nested.definition
      : typeof data.title === "string"
        ? data
        : null;
  if (!raw) return null;
  const title = asString(raw.title);
  const definition = asString(raw.definition);
  if (!title && !definition) return null;
  return {
    section: asString(raw.section),
    pos: asNumber(raw.pos),
    title,
    definition,
    explanation: asString(raw.explanation),
    includes: asStringArray(raw.includes),
  };
};

const ICD_INSTRUCTION_NESTED_KEYS = ["data", "results", "codes", "items"];

const icdInstructionCode = (value: Record<string, unknown>): string => {
  const raw =
    value.code ?? value.Code ?? value.icd10_code ?? value.icdCode ?? value.icd;
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return asString(raw);
};

const isIcdInstructionRow = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) &&
  Boolean(icdInstructionCode(value)) &&
  ("excludes1" in value ||
    "Excludes1" in value ||
    "excludes2" in value ||
    "Excludes2" in value ||
    "includes" in value ||
    "code_first" in value ||
    "codeFirst" in value ||
    "use_additional_code" in value ||
    "useAdditionalCode" in value ||
    "code_also" in value ||
    "codeAlso" in value ||
    "notes" in value ||
    "instructions" in value ||
    "description" in value);

const collectIcdInstructionRows = (
  value: unknown,
  depth = 0
): Record<string, unknown>[] => {
  if (depth > 6 || value == null) return [];
  if (Array.isArray(value)) {
    const rows: Record<string, unknown>[] = [];
    for (const item of value) {
      const nested = collectIcdInstructionRows(item, depth + 1);
      for (let i = 0; i < nested.length; i += 1) rows.push(nested[i]);
    }
    return rows;
  }
  if (!isRecord(value)) return [];
  for (let i = 0; i < ICD_INSTRUCTION_NESTED_KEYS.length; i += 1) {
    const nestedValue = value[ICD_INSTRUCTION_NESTED_KEYS[i]];
    if (nestedValue == null) continue;
    const nested = collectIcdInstructionRows(nestedValue, depth + 1);
    if (nested.length) return nested;
  }
  if (isIcdInstructionRow(value)) return [value];
  return [];
};

const uniqueStrings = (values: string[]): string[] => {
  const out: string[] = [];
  const seen: Record<string, boolean> = {};
  for (let i = 0; i < values.length; i += 1) {
    const item = values[i];
    if (!item || seen[item]) continue;
    seen[item] = true;
    out.push(item);
  }
  return out;
};

const instructionNoteStrings = (
  item: Record<string, unknown>,
  key: string,
  alt?: string
): string[] => {
  const collected: string[] = asStringArray(
    item[key] ?? (alt ? item[alt] : undefined)
  );
  if (!Array.isArray(item.instructions)) return uniqueStrings(collected);
  for (let i = 0; i < item.instructions.length; i += 1) {
    const instruction = item.instructions[i];
    if (!isRecord(instruction)) continue;
    const extra = asStringArray(
      instruction[key] ?? (alt ? instruction[alt] : undefined)
    );
    for (let j = 0; j < extra.length; j += 1) collected.push(extra[j]);
  }
  return uniqueStrings(collected);
};

const mergeSevenChrDef = (item: Record<string, unknown>): Record<string, string> => {
  const out: Record<string, string> = {};
  const apply = (value: unknown) => {
    if (!isRecord(value)) return;
    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const text = asString(value[key]);
      if (text) out[key] = text;
    }
  };
  apply(item.seven_chr_def ?? item.sevenChrDef);
  if (Array.isArray(item.instructions)) {
    for (let i = 0; i < item.instructions.length; i += 1) {
      const instruction = item.instructions[i];
      if (isRecord(instruction)) {
        apply(instruction.seven_chr_def ?? instruction.sevenChrDef);
      }
    }
  }
  return out;
};

const toAggregatedIcdCode = (
  item: Record<string, unknown>
): AggregatedIcdCode | null => {
  const code = icdInstructionCode(item);
  if (!code) return null;
  return {
    code,
    description: asString(item.description),
    excludes1: instructionNoteStrings(item, "excludes1", "Excludes1"),
    excludes2: instructionNoteStrings(item, "excludes2", "Excludes2"),
    includes: instructionNoteStrings(item, "includes", "Includes"),
    inclusion_term: instructionNoteStrings(
      item,
      "inclusion_term",
      "inclusionTerm"
    ),
    seven_chr_def: mergeSevenChrDef(item),
    seven_chr_note: instructionNoteStrings(
      item,
      "seven_chr_note",
      "sevenChrNote"
    ),
    use_additional_code: instructionNoteStrings(
      item,
      "use_additional_code",
      "useAdditionalCode"
    ),
    code_first: instructionNoteStrings(item, "code_first", "codeFirst"),
    code_also: instructionNoteStrings(item, "code_also", "codeAlso"),
    notes: instructionNoteStrings(item, "notes", "Notes"),
  };
};

const asOptions = (value: unknown): EncoderLabeledOption[] => {
  if (!Array.isArray(value)) return [];
  const options: EncoderLabeledOption[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const optionValue = asString(item.value || item.code);
    if (!optionValue) continue;
    options.push({
      value: optionValue,
      label: asString(item.label, optionValue),
      code: typeof item.code === "string" ? item.code : undefined,
    });
  }
  return options;
};

export const extractEncoderCatalog = (payload: unknown): EncoderCatalog => {
  const data = unwrapEncoderPayload(payload);
  if (!data) return DEFAULT_ENCODER_CATALOG;

  const tabs: EncoderCatalogTab[] = [];
  if (Array.isArray(data.tabs)) {
    for (const tab of data.tabs) {
      if (!isRecord(tab)) continue;
      const id = asString(tab.id);
      const label = asString(tab.label);
      if (!id || !label) continue;
      tabs.push({ id, label });
    }
  }

  const codeYears = Array.isArray(data.codeYears)
    ? data.codeYears.map((year) => asNumber(year)).filter((year) => year > 0)
    : DEFAULT_CODE_YEARS;

  const codeTypes = Array.isArray(data.codeTypes)
    ? (data.codeTypes.filter((type) =>
        ["All", "Dx", "PCS", "CPT", "HCPCS"].includes(String(type))
      ) as EncoderCodeType[])
    : DEFAULT_CODE_TYPES;

  return {
    success: data.success !== false,
    error: typeof data.error === "string" ? data.error : undefined,
    module: asString(data.module, "encoder"),
    title: asString(data.title, DEFAULT_ENCODER_CATALOG.title),
    description: asString(data.description, DEFAULT_ENCODER_CATALOG.description),
    tabs: tabs.length > 0 ? tabs : DEFAULT_ENCODER_TABS,
    codeYears: codeYears.length > 0 ? codeYears : DEFAULT_CODE_YEARS,
    codeTypes: codeTypes.length > 0 ? codeTypes : DEFAULT_CODE_TYPES,
    labelColors: isRecord(data.labelColors)
      ? { ...DEFAULT_LABEL_COLORS, ...(data.labelColors as object) }
      : { ...DEFAULT_LABEL_COLORS },
    poaOptions: asOptions(data.poaOptions).length
      ? asOptions(data.poaOptions)
      : DEFAULT_POA_OPTIONS,
    sexOptions: asOptions(data.sexOptions).length
      ? asOptions(data.sexOptions)
      : DEFAULT_SEX_OPTIONS,
    dischargeDispositions: asOptions(data.dischargeDispositions).length
      ? asOptions(data.dischargeDispositions)
      : DEFAULT_DISCHARGE_DISPOSITIONS,
  };
};

export const extractCodeSearchResults = (payload: unknown): CodeSearchResult[] => {
  const data = unwrapEncoderPayload(payload);
  const results: CodeSearchResult[] = [];
  for (const item of listFrom(data, "results")) {
    if (!isRecord(item)) continue;
    const code = asString(item.code);
    if (!code) continue;
    results.push({
      code,
      description: asString(item.description),
      labels: asStringArray(item.labels),
      type: asString(item.type, "Dx"),
      metadata: isRecord(item.metadata)
        ? (item.metadata as CodeSearchResult["metadata"])
        : undefined,
      _formatted: isRecord(item._formatted)
        ? {
            code: asString(item._formatted.code, code),
            description: asString(item._formatted.description),
            labels: asStringArray(item._formatted.labels),
            type: asString(item._formatted.type),
          }
        : undefined,
    });
  }
  return results;
};

const walkEncoderRecords = (
  value: unknown,
  onRecord: (item: Record<string, unknown>) => void,
  depth = 0
) => {
  if (depth > 8 || value == null) return;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      walkEncoderRecords(value[i], onRecord, depth + 1);
    }
    return;
  }
  if (!isRecord(value)) return;
  onRecord(value);
  const keys = Object.keys(value);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (key === "_formatted" || key === "metadata" || key === "combo") continue;
    walkEncoderRecords(value[key], onRecord, depth + 1);
  }
};

const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!text || (text.charAt(0) !== "{" && text.charAt(0) !== "[")) return value;
  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
};

const instructionListFromPayload = (payload: unknown, depth = 0): unknown[] => {
  const value = parseJsonIfString(payload);
  if (depth > 6 || value == null) return [];
  if (Array.isArray(value)) return value;
  if (!isRecord(value)) return [];
  // Keep the parent ICD row. Nested `instructions` use category codes (B16)
  // and must not replace the billed code (B160) as the lookup key.
  if (
    icdInstructionCode(value) &&
    ("description" in value ||
      "instructions" in value ||
      "excludes1" in value ||
      "Excludes1" in value ||
      "includes" in value)
  ) {
    return [value];
  }
  const keys = ["data", "results", "codes", "items"];
  for (let i = 0; i < keys.length; i += 1) {
    const nested = instructionListFromPayload(value[keys[i]], depth + 1);
    if (nested.length) return nested;
  }
  const objectKeys = Object.keys(value);
  if (
    objectKeys.length &&
    objectKeys.every((key) => /^\d+$/.test(key))
  ) {
    const list: unknown[] = [];
    for (let i = 0; i < objectKeys.length; i += 1) list.push(value[objectKeys[i]]);
    return instructionListFromPayload(list, depth + 1);
  }
  if (icdInstructionCode(value)) return [value];
  return [];
};

const instructionRichness = (item: AggregatedIcdCode): number =>
  (item.excludes1?.length || 0) +
  (item.excludes2?.length || 0) +
  (item.includes?.length || 0) +
  (item.inclusion_term?.length || 0) +
  (item.use_additional_code?.length || 0) +
  (item.code_first?.length || 0) +
  (item.code_also?.length || 0) +
  (item.notes?.length || 0);

export const extractAggregatedIcdCodes = (
  payload: unknown
): AggregatedIcdCode[] => {
  const found: AggregatedIcdCode[] = [];
  const indexByCode: Record<string, number> = {};
  const addMapped = (mapped: AggregatedIcdCode | null) => {
    if (!mapped) return;
    const key = normalizeEncoderCode(mapped.code);
    if (!key) return;
    const existingIndex = indexByCode[key];
    if (existingIndex == null) {
      indexByCode[key] = found.length;
      found.push(mapped);
      return;
    }
    if (instructionRichness(mapped) > instructionRichness(found[existingIndex])) {
      found[existingIndex] = mapped;
    }
  };

  const lists: unknown[][] = [
    instructionListFromPayload(payload),
    collectIcdInstructionRows(unwrapEncoderPayload(payload) ?? payload),
  ];
  for (let listIndex = 0; listIndex < lists.length; listIndex += 1) {
    const list = lists[listIndex];
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      if (isRecord(item)) addMapped(toAggregatedIcdCode(item));
    }
  }
  walkEncoderRecords(payload, (item) => addMapped(toAggregatedIcdCode(item)));
  return found;
};

const collectEncoderResultRecords = (
  payload: unknown,
  extraKeys: string[]
): Record<string, unknown>[] => {
  if (Array.isArray(payload)) {
    const rows: Record<string, unknown>[] = [];
    for (let i = 0; i < payload.length; i += 1) {
      const item = payload[i];
      if (isRecord(item)) rows.push(item);
    }
    return rows;
  }
  const data = unwrapEncoderPayload(payload);
  const keys = ["results"].concat(extraKeys);
  for (let k = 0; k < keys.length; k += 1) {
    const list = listFrom(data, keys[k]);
    if (!list.length) continue;
    const rows: Record<string, unknown>[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      if (isRecord(item)) rows.push(item);
    }
    if (rows.length) return rows;
  }
  if (data && (data.code_1 != null || data.code1 != null || data.column1 != null)) {
    return [data];
  }
  return [];
};

const looksLikeValidationRow = (item: Record<string, unknown>): boolean => {
  if (item.combo != null || item.column1 != null) return false;
  const hasInstruction =
    typeof item.instruction === "string" && item.instruction.trim().length > 0;
  const hasSeverity =
    typeof item.severity === "string" && item.severity.trim().length > 0;
  const hasMessage =
    typeof item.message === "string" && item.message.trim().length > 0;
  const hasNote = typeof item.note === "string" && item.note.trim().length > 0;
  const hasCodes = item.code_1 != null || item.code1 != null || item.code_2 != null;
  return Boolean((hasInstruction || hasSeverity) && (hasMessage || hasNote || hasCodes));
};

const toValidationRow = (
  item: Record<string, unknown>
): EncoderIcdValidationResult => ({
  instruction: asString(item.instruction),
  code_1: asString(item.code_1 ?? item.code1 ?? item.code),
  code_1_description: asString(
    item.code_1_description ?? item.code1Description
  ),
  code_2: asString(item.code_2 ?? item.code2),
  code_2_description: asString(
    item.code_2_description ?? item.code2Description
  ),
  note: asString(item.note),
  message: asString(item.message),
  severity: asString(item.severity),
});

export const extractIcdValidationResults = (
  payload: unknown
): EncoderIcdValidationResult[] => {
  const rows: EncoderIcdValidationResult[] = [];
  const seen: Record<string, boolean> = {};
  const add = (item: Record<string, unknown>) => {
    if (!looksLikeValidationRow(item)) return;
    const mapped = toValidationRow(item);
    const key = [
      mapped.instruction,
      mapped.code_1,
      mapped.code_2,
      mapped.message,
      mapped.note,
      mapped.severity,
    ].join("|");
    if (seen[key]) return;
    seen[key] = true;
    rows.push(mapped);
  };

  const items = collectEncoderResultRecords(payload, [
    "issues",
    "validations",
    "data",
  ]);
  for (let i = 0; i < items.length; i += 1) add(items[i]);
  if (!rows.length) {
    walkEncoderRecords(payload, add);
  }
  return rows;
};

export const alertsFromIcdInstructions = (
  code: string,
  icd: AggregatedIcdCode | null
): EncoderIcdValidationResult[] => {
  if (!icd || !code) return [];
  const rows: EncoderIcdValidationResult[] = [];
  const push = (instruction: string, message: string, notes: string[]) => {
    if (!notes.length) return;
    rows.push({
      instruction,
      code_1: code,
      code_1_description: icd.description,
      code_2: "",
      code_2_description: "",
      message,
      note: notes.join("; "),
      severity: "warning",
    });
  };
  push(
    "useAdditionalCode",
    `Code ${code} requires an additional code to fully describe the condition.`,
    icd.use_additional_code
  );
  push(
    "codeFirst",
    `Code ${code} should be sequenced after the underlying condition.`,
    icd.code_first
  );
  push(
    "codeAlso",
    `Code ${code} may need an additional code to fully describe the condition.`,
    icd.code_also
  );
  return rows;
};

export const extractCptEditsResults = (payload: unknown): CptEditsResult[] => {
  const rows: CptEditsResult[] = [];
  const items = collectEncoderResultRecords(payload, ["edits"]);
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const comboSource = Array.isArray(item.combo) ? item.combo : [];
    const combo: CptEditsResult["combo"] = [];
    for (let j = 0; j < comboSource.length; j += 1) {
      const entry = comboSource[j];
      if (!isRecord(entry)) continue;
      combo.push({
        code: asString(entry.code),
        rationale: asString(entry.rationale),
        modifierIndicator: asNumber(entry.modifierIndicator),
        RVU: asString(entry.RVU ?? entry.rvu),
        modifiers: asStringArray(entry.modifiers),
        type: asString(entry.type),
      });
    }
    rows.push({
      column1: asString(item.column1 ?? item.code),
      RVU: asString(item.RVU ?? item.rvu),
      combo,
    });
  }
  return rows;
};

export const findAggregatedIcdCode = (
  list: AggregatedIcdCode[] | unknown,
  code: string
): AggregatedIcdCode | null => {
  if (!code) return null;
  const rows = extractAggregatedIcdCodes(list);
  if (!rows.length) return null;
  const needle = normalizeEncoderCode(code);
  for (let i = 0; i < rows.length; i += 1) {
    if (normalizeEncoderCode(rows[i].code) === needle) return rows[i];
  }
  return rows.length === 1 ? rows[0] : null;
};

export const extractYears = (payload: unknown): number[] => {
  const data = unwrapEncoderPayload(payload);
  const years = listFrom(data, "years");
  return years
    .map((year) => (isRecord(year) ? asNumber(year.year) : asNumber(year)))
    .filter((year) => year > 0);
};

export const extractCmYears = (
  payload: unknown
): { years: number[]; defaultYear: number } => {
  const data = unwrapEncoderPayload(payload);
  const years = extractYears(payload);
  return {
    years,
    defaultYear: asNumber(data?.defaultYear, years[0] ?? 2026),
  };
};

export const extractStringList = (
  payload: unknown,
  key: string
): string[] => {
  const data = unwrapEncoderPayload(payload);
  return asStringArray(data?.[key]);
};

const emptyNotes = (): CmTabularNotes => ({
  includes: [],
  inclusionTerm: [],
  excludes1: [],
  excludes2: [],
  codeFirst: [],
  useAdditionalCode: [],
  codeAlso: [],
  notes: [],
});

const extractNotes = (value: unknown): CmTabularNotes => {
  if (!isRecord(value)) return emptyNotes();
  return {
    includes: asStringArray(value.includes),
    inclusionTerm: asStringArray(value.inclusionTerm),
    excludes1: asStringArray(value.excludes1),
    excludes2: asStringArray(value.excludes2),
    codeFirst: asStringArray(value.codeFirst),
    useAdditionalCode: asStringArray(value.useAdditionalCode),
    codeAlso: asStringArray(value.codeAlso),
    notes: asStringArray(value.notes),
  };
};

const extractDotlessCodes = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const codes: string[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const code = String(value[i] ?? "")
      .replace(/\./g, "")
      .replace(/-+$/, "")
      .toUpperCase()
      .trim();
    if (code) codes.push(code);
  }
  return codes;
};

const extractTabularNoteHits = (value: unknown): CmTabularNoteHit[] => {
  if (!Array.isArray(value)) return [];
  const hits: CmTabularNoteHit[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const item = value[i];
    if (!isRecord(item)) continue;
    const noteType =
      asString(item.noteType) === "includes" ? "includes" : "inclusionTerm";
    const hit: CmTabularNoteHit = {
      id: asString(item.id),
      title: asString(item.title),
      code: asString(item.code),
      noteType,
      score: asNumber(item.score),
    };
    if (hit.title || hit.code) hits.push(hit);
  }
  return hits;
};

export const extractCmSearchHits = (
  payload: unknown
): {
  results: CmSearchHit[];
  expandedTerm: string | null;
  tabularNotes: CmTabularNoteHit[];
  queryWords: string[];
} => {
  const data = unwrapEncoderPayload(payload);
  const hits: CmSearchHit[] = [];
  for (const item of listFrom(data, "results")) {
    if (!isRecord(item)) continue;
    const redirectTargets: CmSearchHit["redirectTargets"] = [];
    if (Array.isArray(item.redirectTargets)) {
      for (const target of item.redirectTargets) {
        if (!isRecord(target)) continue;
        redirectTargets.push({
          title: asString(target.title),
          nemod: asString(target.nemod),
          code: asString(target.code),
        });
      }
    }
    const hit: CmSearchHit = {
      id: asString(item.id),
      title: asString(item.title),
      nemod: asString(item.nemod),
      score: asNumber(item.score),
      isExact: Boolean(item.isExact),
      code: asString(item.code),
      see: asString(item.see),
      seeAlso: asString(item.seeAlso),
      hasChildren: Boolean(item.hasChildren),
      source: asString(item.source, "index") as CmSearchHit["source"],
      redirectTargets,
    };
    if (hit.id || hit.title) hits.push(hit);
  }
  return {
    expandedTerm:
      typeof data?.expandedTerm === "string" ? data.expandedTerm : null,
    tabularNotes: extractTabularNoteHits(data?.tabularNotes),
    queryWords: asStringArray(data?.queryWords),
    results: hits,
  };
};

const extractIndexNode = (value: unknown): CmIndexTermNode | null => {
  if (!isRecord(value)) return null;
  const id = asString(value.id);
  const title = asString(value.title);
  if (!id && !title) return null;
  return {
    id,
    title,
    nemod: asString(value.nemod),
    code: asString(value.code),
    see: asString(value.see),
    seeAlso: asString(value.seeAlso),
    manif: asString(value.manif),
    subcat: asString(value.subcat),
    seecat: asString(value.seecat),
    level: asNumber(value.level),
    children: Array.isArray(value.children)
      ? value.children
          .map(extractIndexNode)
          .filter((node): node is CmIndexTermNode => Boolean(node))
      : [],
  };
};

export const extractCmIndexTree = (
  payload: unknown
): { tree: CmIndexTermNode | null; seventhCharCodes: string[] } => {
  const data = unwrapEncoderPayload(payload);
  return {
    tree: extractIndexNode(data?.tree ?? data),
    seventhCharCodes: extractDotlessCodes(data?.seventhCharCodes),
  };
};

const extractTableNode = (value: unknown): CmTableTermNode | null => {
  if (!isRecord(value)) return null;
  const cells = Array.isArray(value.cells)
    ? (value.cells.map((cell) =>
        cell == null ? null : String(cell)
      ) as CmTableTermNode["cells"])
    : ([null, null, null, null, null, null] as CmTableTermNode["cells"]);
  while (cells.length < 6) cells.push(null);
  return {
    id: asString(value.id),
    title: asString(value.title),
    nemod: asString(value.nemod),
    see: asString(value.see),
    seeAlso: asString(value.seeAlso),
    level: asNumber(value.level),
    cells: cells.slice(0, 6) as CmTableTermNode["cells"],
    children: Array.isArray(value.children)
      ? value.children
          .map(extractTableNode)
          .filter((node): node is CmTableTermNode => Boolean(node))
      : [],
  };
};

export const extractCmTableTrees = (
  payload: unknown
): { trees: CmTableTermNode[]; total: number; seventhCharCodes: string[] } => {
  const data = unwrapEncoderPayload(payload);
  const trees = listFrom(data, "trees")
    .map(extractTableNode)
    .filter((node): node is CmTableTermNode => Boolean(node));
  return {
    trees,
    total: asNumber(data?.total, trees.length),
    seventhCharCodes: extractDotlessCodes(data?.seventhCharCodes),
  };
};

export const extractCmTabularSearchHits = (
  payload: unknown
): CmTabularSearchHit[] => {
  const data = unwrapEncoderPayload(payload);
  const hits: CmTabularSearchHit[] = [];
  for (const item of listFrom(data, "results")) {
    if (!isRecord(item)) continue;
    const hit: CmTabularSearchHit = {
      code: asString(item.code),
      description: asString(item.description),
      isPlaceholder: Boolean(item.isPlaceholder),
      hasChildren: Boolean(item.hasChildren),
      chapter: asString(item.chapter),
      sectionId: asString(item.sectionId),
    };
    if (hit.code) hits.push(hit);
  }
  return hits;
};

export const extractCmTabular = (payload: unknown): CmTabularResult | null => {
  const data = unwrapEncoderPayload(payload);
  const result = data && isRecord(data.result) ? data.result : data;
  if (!isRecord(result) || !asString(result.code)) return null;
  return {
    code: asString(result.code),
    cleanCode: asString(result.cleanCode, asString(result.code)),
    description: asString(result.description),
    isPlaceholder: Boolean(result.isPlaceholder),
    hasChildren: Boolean(result.hasChildren),
    chapter: asString(result.chapter),
    sectionId: asString(result.sectionId),
    ancestors: Array.isArray(result.ancestors)
      ? result.ancestors
          .filter(isRecord)
          .map((ancestor) => ({
            code: asString(ancestor.code),
            description: asString(ancestor.description),
            isPlaceholder: Boolean(ancestor.isPlaceholder),
            notes: extractNotes(ancestor.notes),
          }))
      : [],
    children: Array.isArray(result.children)
      ? result.children
          .filter(isRecord)
          .map((child) => ({
            code: asString(child.code),
            description: asString(child.description),
            hasChildren: Boolean(child.hasChildren),
            isPlaceholder: Boolean(child.isPlaceholder),
          }))
      : [],
    notes: extractNotes(result.notes),
    seventhChars: Array.isArray(result.seventhChars)
      ? result.seventhChars
          .filter(isRecord)
          .map((item) => ({
            char: asString(item.char),
            meaning: asString(item.meaning),
          }))
      : [],
    seventhCharParentCode:
      typeof result.seventhCharParentCode === "string"
        ? result.seventhCharParentCode
        : null,
    isBillable: Boolean(result.isBillable),
    needsSeventhChar: Boolean(result.needsSeventhChar),
  };
};

export const extractIcdGuidelines = (payload: unknown): IcdGuideline[] => {
  const results = listEncoderResults(payload);
  const guidelines: IcdGuideline[] = [];
  for (const item of results) {
    if (!isRecord(item)) continue;
    const id = asNumber(item.id);
    if (!id) continue;
    guidelines.push({
      id,
      fyear: item.fyear == null ? null : asNumber(item.fyear),
      guidelineType: optionalString(item.guidelineType ?? item.guideline_type),
      chapter: optionalString(item.chapter),
      subChapter: optionalString(item.subChapter ?? item.sub_chapter),
      topic: optionalString(item.topic),
      subTopic: optionalString(item.subTopic ?? item.sub_topic),
      keywords: optionalString(item.keywords),
      codingNote: optionalString(item.codingNote ?? item.coding_note),
    });
  }
  return guidelines;
};

export const extractAhaGuidelines = (payload: unknown): AhaGuideline[] => {
  const results = listEncoderResults(payload);
  const guidelines: AhaGuideline[] = [];
  for (const item of results) {
    if (!isRecord(item)) continue;
    const id = asNumber(item.id);
    if (!id) continue;
    const qaSource = Array.isArray(item.qaPairs)
      ? item.qaPairs
      : Array.isArray(item.qa_pairs)
        ? item.qa_pairs
        : null;
    guidelines.push({
      id,
      fyear: item.fyear == null ? null : asNumber(item.fyear),
      fyQuater: optionalString(
        item.fyQuater ?? item.fy_quater ?? item.fyQuarter
      ),
      clinicalPageNo: optionalString(
        item.clinicalPageNo ?? item.clinical_page_no
      ),
      keywords: optionalString(item.keywords),
      codingClinicOverview: optionalString(
        item.codingClinicOverview ?? item.coding_clinic_overview
      ),
      codingClinicQa: optionalString(
        item.codingClinicQa ?? item.coding_clinic_qa
      ),
      informationStatement: optionalString(
        item.informationStatement ?? item.information_statement
      ),
      qaPairs: qaSource
        ? qaSource.filter(isRecord).map((pair) => ({
            question: asString(pair.question ?? pair.Question),
            answer: asString(pair.answer ?? pair.Answer),
          }))
        : null,
    });
  }
  return guidelines;
};

export const extractClinicalCriteriaFiles = (
  payload: unknown
): ClinicalCriteriaFileMeta[] => {
  const data = unwrapEncoderPayload(payload);
  const nested = data && isRecord(data.data) ? data.data : null;
  const files =
    data && Array.isArray(data.files)
      ? data.files
      : nested && Array.isArray(nested.files)
        ? nested.files
        : [];
  const list: ClinicalCriteriaFileMeta[] = [];
  for (const file of files) {
    if (!isRecord(file)) continue;
    const meta = { id: asString(file.id), name: asString(file.name) };
    if (meta.id || meta.name) list.push(meta);
  }
  return list;
};

export const extractClinicalCriteriaFile = (
  payload: unknown
): ClinicalCriteriaFileContent | null => {
  const data = unwrapEncoderPayload(payload);
  if (!data) return null;
  const nested = isRecord(data.data) ? data.data : null;
  const file = isRecord(data.file)
    ? data.file
    : nested && isRecord(nested.file)
      ? nested.file
      : data;
  if (!isRecord(file)) return null;
  const markdown = optionalString(
    file.markdown,
    nested && nested.markdown,
    data.markdown
  );
  const html = optionalString(file.html, nested && nested.html, data.html);
  return {
    id: typeof file.id === "string" ? file.id : undefined,
    name: typeof file.name === "string" ? file.name : undefined,
    markdown: markdown || undefined,
    html: html,
  };
};

export const sanitizeGuidelineText = (
  text: string | null | undefined
): string => {
  if (!text) return "";
  return text
    .replace(/\uFFFD/g, "'")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u2013/g, "-")
    .replace(/\u2014/g, "--");
};

const removeAhaControlCharacters = (text: string): string =>
  Array.from(text)
    .map((character) => {
      if (character === "\n" || character === "\r" || character === "\t") {
        return character;
      }
      const code = character.charCodeAt(0);
      if (code === 0xfffd || code < 32 || code === 127) return "";
      if (character === "®") return "(R)";
      if (character === "™" || character === "℠") return "(TM)";
      if (character === "©") return "(C)";
      return character;
    })
    .join("");

const normalizeAhaSourceText = (text: string): string =>
  removeAhaControlCharacters(text)
    .replace(/([0-9A-HJ-NP-Z]{7})([A-Za-z])/g, "$1 $2")
    .replace(/;([0-9A-HJ-NP-Z]{7})/g, "; $1");

export const cleanAhaText = (text: string | null | undefined): string => {
  if (!text) return "";
  return normalizeAhaSourceText(sanitizeGuidelineText(text))
    .replace(/\s+/g, " ")
    .trim();
};

export const cleanAhaMultilineText = (
  text: string | null | undefined
): string => {
  if (!text) return "";
  return normalizeAhaSourceText(sanitizeGuidelineText(text))
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const formatAhaClinicalPageNo = (
  pageNo: string | null | undefined
): string => {
  if (!pageNo || !pageNo.trim()) return "";
  let text = sanitizeGuidelineText(pageNo.trim()).replace(/\s+/g, " ").trim();
  if (!text) return "";
  const normalizePageSeparators = (value: string) =>
    value
      .replace(/(\d)\s*(.)\s*(\d)/g, (match, left, sep, right) => {
        const code = sep.charCodeAt(0);
        if (code === 0xfffd || code < 32 || code === 127) {
          return `${left},${right}`;
        }
        return match;
      })
      .replace(/(\d)\s*[''\u2018\u2019\u2013\u2014;/\-–—]\s*(\d)/g, "$1,$2");
  let previous = "";
  while (previous !== text) {
    previous = text;
    text = normalizePageSeparators(text);
  }
  if (/^\d{4}$/.test(text)) {
    const left = text.slice(0, 2);
    const right = text.slice(2);
    const leftNum = Number.parseInt(left, 10);
    const rightNum = Number.parseInt(right, 10);
    if (leftNum >= 1 && leftNum <= 99 && rightNum >= 1 && rightNum <= 99) {
      return `${left},${right}`;
    }
  }
  return text;
};

export const formatAhaGuidelineTitle = (guideline: AhaGuideline): string => {
  const overview = cleanAhaText(guideline.codingClinicOverview);
  if (overview) return overview;
  const keywords = cleanAhaText(guideline.keywords);
  if (keywords && !/^aha coding clinic( reference)?$/i.test(keywords)) {
    return keywords;
  }
  return "AHA Coding Clinic Reference";
};

export const formatAhaYearQuarterPage = (guideline: AhaGuideline): string => {
  if (!guideline.fyear || !guideline.fyQuater) return "";
  const quarter = cleanAhaText(guideline.fyQuater);
  const page = formatAhaClinicalPageNo(guideline.clinicalPageNo);
  return `[${guideline.fyear} ${quarter}${page ? `, Page ${page}` : ""}]`;
};

const parseAhaCodingClinicQa = (qaText: string): AhaQaPair => {
  if (!qaText.trim()) return { question: "", answer: "" };
  const questionMatch =
    qaText.match(/\bQuestions?\s*:?\s*/i) ?? qaText.match(/\bQ\s*:\s*/i);
  const answerMatch =
    qaText.match(/\bAnswers?\s*:?\s*/i) ?? qaText.match(/\bA\s*:\s*/i);
  if (
    questionMatch &&
    answerMatch &&
    questionMatch.index !== undefined &&
    answerMatch.index !== undefined &&
    answerMatch.index > questionMatch.index
  ) {
    return {
      question: cleanAhaText(
        qaText.substring(
          questionMatch.index + questionMatch[0].length,
          answerMatch.index
        )
      ),
      answer: cleanAhaText(
        qaText.substring(answerMatch.index + answerMatch[0].length)
      ),
    };
  }
  if (questionMatch && questionMatch.index !== undefined) {
    return {
      question: cleanAhaText(
        qaText.substring(questionMatch.index + questionMatch[0].length)
      ),
      answer: "",
    };
  }
  if (answerMatch && answerMatch.index !== undefined) {
    return {
      question: "",
      answer: cleanAhaText(
        qaText.substring(answerMatch.index + answerMatch[0].length)
      ),
    };
  }
  return { question: "", answer: cleanAhaText(qaText) };
};

export const sanitizeHighlightHtml = (html: string): string => {
  if (!html) return "";
  return sanitizeGuidelineHtml(html);
};

export const labelColorClass = (label: string): string => {
  const key = label.split("-")[0] as keyof typeof LABEL_COLOR_CLASSES;
  if (label === "IP Only") return LABEL_COLOR_CLASSES["IP Only"];
  return LABEL_COLOR_CLASSES[key] || "bg-gray-200 text-gray-700";
};

export const labelStyle = (
  label: string,
  colors?: Record<string, { background: string; color: string }>
): { background: string; color: string } | undefined => {
  if (!colors) return undefined;
  if (colors[label]) return colors[label];
  const prefix = label.split("-")[0];
  if (prefix && colors[prefix]) return colors[prefix];
  if (label.startsWith("Rx") && colors.Rx) return colors.Rx;
  if (label.startsWith("HCC") && colors.HCC) return colors.HCC;
  return undefined;
};

export const todayDdMmYyyy = (): string => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
};

export const isoDate = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

export const daysBetween = (from: Date, to: Date): number => {
  const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.max(0, Math.round((end - start) / 86_400_000));
};

export const ageInYearsFromBirthDate = (
  birthDate: string,
  asOf: Date = new Date()
): number | null => {
  const born = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(born.getTime())) return null;
  let age = asOf.getFullYear() - born.getFullYear();
  const month = asOf.getMonth() - born.getMonth();
  if (month < 0 || (month === 0 && asOf.getDate() < born.getDate())) {
    age -= 1;
  }
  return Math.max(0, age);
};

export const mapFhirGender = (gender?: string | null): MsDrgSex => {
  const value = gender?.trim().toLowerCase();
  if (value === "male" || value === "m") return "MALE";
  if (value === "female" || value === "f") return "FEMALE";
  return "UNKNOWN";
};

export const dischargeStatusCode = (
  option: EncoderLabeledOption | undefined,
  fallback = "01"
): string => {
  const code = option?.code || option?.value || fallback;
  return code.padStart(2, "0");
};

export const elixhauserDispositionCode = (cmsCode: string): string =>
  String(Number.parseInt(cmsCode, 10) || 1);

export const currentQuarter = (date = new Date()): number =>
  Math.floor(date.getMonth() / 3) + 1;

export const newScratchId = (): string =>
  `code-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const toScratchCode = (result: CodeSearchResult): EncoderScratchCode => ({
  id: newScratchId(),
  code: result.code,
  description: result.description,
  type: result.type,
  labels: result.labels,
  metadata: result.metadata,
});

export type EncoderMsDrgProfile = {
  id: string;
  name: string;
  sex: MsDrgSex;
  ageAtAdmission: number;
  admitDate: string;
  dischargeDate: string;
  dischargeStatus: string;
};

export const getDefaultEncoderMsDrgProfiles = (): EncoderMsDrgProfile[] => [
  {
    id: "default-profile-1",
    name: "Male Patient",
    sex: "MALE",
    ageAtAdmission: 65,
    admitDate: "2026-02-12",
    dischargeDate: "2026-02-18",
    dischargeStatus: "01",
  },
  {
    id: "default-profile-2",
    name: "Female Patient",
    sex: "FEMALE",
    ageAtAdmission: 73,
    admitDate: "2026-03-12",
    dischargeDate: "2026-03-25",
    dischargeStatus: "03",
  },
];

export const parsePastedCodes = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((token) => token.trim().toUpperCase())
    .filter((token) => token.length >= 3);

export const isPcsLike = (code: string): boolean =>
  /^[0-9A-HJ-NP-Z]{7}$/i.test(code.replace(/[.\s]/g, ""));

export const isIcd10DxLike = (code: string): boolean =>
  /^[A-Z][0-9][A-Z0-9]{1,6}$/i.test(code.replace(/[.\s]/g, ""));

export const isCptLike = (code: string): boolean => /^\d{5}$/.test(code);

export const ENCODER_TOOL_IDS: EncoderTabId[] = [
  "code-search",
  "coding-guide",
  "clinical-criteria",
  "cm-explorer",
  "pcs-explorer",
  "cpt-explorer",
  "editor",
  "ms-drg-grouper",
  "mcd-coverage",
];

export const encoderHref = (tool?: EncoderTabId): string =>
  tool ? `${RouteConstants.ROUTE_ENCODER}/${tool}` : RouteConstants.ROUTE_ENCODER;

export const parseEncoderPathname = (pathname: string): EncoderRouteId => {
  const base = RouteConstants.ROUTE_ENCODER;
  const trimmed =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  if (trimmed === base) return "home";
  if (!trimmed.startsWith(`${base}/`)) return "home";
  const rest = trimmed.slice(base.length + 1).split("/")[0];
  return normalizeTabId(rest) ?? "home";
};

export const normalizeTabId = (id: string): EncoderTabId | null => {
  if (ENCODER_TOOL_IDS.includes(id as EncoderTabId)) return id as EncoderTabId;
  const aliases: Record<string, EncoderTabId> = {
    "clinical-criterias": "clinical-criteria",
    "icd-10-cm-builder": "cm-explorer",
    "icd-10-pcs-builder": "pcs-explorer",
    "code-editor": "editor",
    "msdrg-grouper": "ms-drg-grouper",
    "cpt-hcpcs-editor": "cpt-explorer",
    "cpt-editor": "cpt-explorer",
  };
  return aliases[id] ?? null;
};

export const parseAhaQaPairs = (guideline: AhaGuideline): AhaQaPair[] => {
  if (Array.isArray(guideline.qaPairs)) {
    const pairs = guideline.qaPairs
      .map((pair) => ({
        question: cleanAhaText(pair.question),
        answer: cleanAhaText(pair.answer),
      }))
      .filter((pair) => pair.question.length > 0 || pair.answer.length > 0);
    if (pairs.length > 0) return pairs;
  }

  const raw = guideline.codingClinicQa;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      const pairs = parsed
        .filter(isRecord)
        .map((pair) => ({
          question: cleanAhaText(asString(pair.question || pair.q)),
          answer: cleanAhaText(asString(pair.answer || pair.a)),
        }))
        .filter((pair) => pair.question || pair.answer);
      if (pairs.length) return pairs;
    }
  } catch {
    // Fall through to Question/Answer label parsing.
  }
  const legacyPair = parseAhaCodingClinicQa(raw);
  return legacyPair.question || legacyPair.answer ? [legacyPair] : [];
};

export const getAhaExpandedQaContent = (
  guideline: AhaGuideline
): { qaPairs: AhaQaPair[]; fallbackText: string } => {
  const qaPairs = parseAhaQaPairs(guideline);
  if (qaPairs.length > 0) {
    return { qaPairs, fallbackText: "" };
  }
  return {
    qaPairs: [],
    fallbackText: cleanAhaMultilineText(guideline.codingClinicQa),
  };
};

export const criteriaFileKey = (file: ClinicalCriteriaFileMeta): string =>
  `${file.id}\0${file.name}`;

export const encoderQueryParams = (
  params: Record<string, unknown>
): Record<string, string | number | boolean | string[]> => {
  const next: Record<string, string | number | boolean | string[]> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      const items = value.map((item) => String(item).trim()).filter(Boolean);
      if (items.length) next[key] = items;
      return;
    }
    if (typeof value === "boolean" || typeof value === "number") {
      next[key] = value;
      return;
    }
    next[key] = String(value);
  });
  return next;
};

const fhirCoding = (
  resource: Record<string, unknown>
): { code: string; display: string } | null => {
  const code = isRecord(resource.code) ? resource.code : null;
  const coding = code && Array.isArray(code.coding) ? code.coding : [];
  for (const entry of coding) {
    if (!isRecord(entry)) continue;
    const value = asString(entry.code);
    if (!value) continue;
    return {
      code: value,
      display: asString(entry.display || code?.text),
    };
  }
  return null;
};

export const encoderPrefillFromFhir = (input: {
  patient?: unknown;
  encounter?: unknown;
  conditions?: unknown[];
  procedures?: unknown[];
}): EncoderFhirPrefill => {
  const patient = isRecord(input.patient) ? input.patient : {};
  const encounter = isRecord(input.encounter) ? input.encounter : {};
  const period = isRecord(encounter.period) ? encounter.period : {};
  const hospitalization = isRecord(encounter.hospitalization)
    ? encounter.hospitalization
    : {};
  const disposition = isRecord(hospitalization.dischargeDisposition)
    ? hospitalization.dischargeDisposition
    : {};
  const dispositionCoding = Array.isArray(disposition.coding)
    ? disposition.coding
    : [];
  const firstDisposition = isRecord(dispositionCoding[0])
    ? dispositionCoding[0]
    : null;
  const birthDate = typeof patient.birthDate === "string" ? patient.birthDate : null;
  const admitDate = isoDate(
    typeof period.start === "string" ? period.start : null
  );
  const dischargeDate = isoDate(
    typeof period.end === "string" ? period.end : null
  );

  const diagnosisCodes: EncoderScratchCode[] = [];
  for (const resource of input.conditions ?? []) {
    if (!isRecord(resource)) continue;
    const coding = fhirCoding(resource);
    if (!coding) continue;
    diagnosisCodes.push({
      id: newScratchId(),
      code: coding.code,
      description: coding.display,
      type: "Dx",
      labels: [],
    });
  }

  const procedureCodes: EncoderScratchCode[] = [];
  for (const resource of input.procedures ?? []) {
    if (!isRecord(resource)) continue;
    const coding = fhirCoding(resource);
    if (!coding) continue;
    procedureCodes.push({
      id: newScratchId(),
      code: coding.code,
      description: coding.display,
      type: isCptLike(coding.code) ? "CPT" : "PCS",
      labels: [],
    });
  }

  return {
    diagnosisCodes,
    procedureCodes,
    sex: mapFhirGender(
      typeof patient.gender === "string" ? patient.gender : null
    ),
    birthDate,
    ageInYears: birthDate ? ageInYearsFromBirthDate(birthDate) : null,
    admitDate,
    dischargeDate,
    dischargeStatus:
      asString(firstDisposition?.code || disposition.text) || null,
  };
};

export const emptyTabularNotes = emptyNotes;
export { asStringArray, asNumber, asString, isRecord };
